import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../../core/data/routes';
import { Therapist, Session } from '../../../core/types';
import { AppBar, LoadingWidget, FadeInAnimation, ZeroState } from '../../../core/components/ui';
import { useAppointmentsStore } from '../../appointments/store/appointmentsStore';
import { useHomepageStore } from '../store/homepageStore';
import { formatDate, formatTime, isWithinWindow } from '../../../core/utils/formatters';
import toast from 'react-hot-toast';
import BookingSheet from '../../appointments/pages/BookingSheet';

const DocSummaryPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const therapist = (location.state as any)?.therapist as Therapist | undefined;

  const appStore = useAppointmentsStore();
  const homeStore = useHomepageStore();
  const user = homeStore.retrieve();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    if (!therapist?.therapistId) return;
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [s, c] = await Promise.all([
      appStore.fetchTherapistSessions(therapist!.therapistId!),
      homeStore.getSessionCredit(),
    ]);
    setSessions(s.filter(sess => sess.customerId === user?.id));
    setCredits(c.quantity || 0);
    setLoading(false);
  };

  if (!therapist) {
    return (
      <div className="page">
        <AppBar title="Therapist" onBack={() => navigate(-1)} />
        <ZeroState title="Not Found" message="Therapist information is unavailable." />
      </div>
    );
  }

  const initials = (therapist.name || '').split(' ').map(n => n[0] || '').join('').slice(0, 2).toUpperCase();
  const isAvailable = (therapist.status || '').toLowerCase() === 'available';
  const hasCredits = credits >= (therapist.rate || 1);
  const upcomingSession = sessions.find(s => s.status === 'PENDING' || s.status === 'CONFIRMED');
  const canJoin = upcomingSession?.sessionFrom ? isWithinWindow(upcomingSession.sessionFrom) : false;

  return (
    <div className="page">
      {loading && <LoadingWidget />}
      <AppBar onBack={() => navigate(-1)} />

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Hero banner */}
        <div style={{
          background: 'var(--color-primary)',
          padding: '0 24px 32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}>
          <FadeInAnimation delay={0}>
            <div style={{
              width: 90, height: 90, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, fontSize: 28, color: 'white',
              overflow: 'hidden', border: '3px solid rgba(255,255,255,0.3)',
            }}>
              {therapist.url
                ? <img src={therapist.url} alt={therapist.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : initials}
            </div>
          </FadeInAnimation>

          <FadeInAnimation delay={0.1}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'white' }}>{therapist.name}</div>
              {therapist.organization && (
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{therapist.organization}</div>
              )}
            </div>
          </FadeInAnimation>

          <FadeInAnimation delay={0.2}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <span style={{
                padding: '4px 12px', borderRadius: 99,
                background: isAvailable ? 'rgba(107,186,107,0.25)' : 'rgba(255,255,255,0.15)',
                color: isAvailable ? '#A0F0A0' : 'rgba(255,255,255,0.6)',
                fontSize: 12, fontWeight: 600,
              }}>
                ● {isAvailable ? 'Available' : (therapist.status || 'Unavailable')}
              </span>
              <span style={{
                padding: '4px 12px', borderRadius: 99,
                background: 'rgba(255,255,255,0.15)',
                color: 'white', fontSize: 12, fontWeight: 600,
              }}>
                {therapist.rate} credit{therapist.rate !== 1 ? 's' : ''}/session
              </span>
            </div>
          </FadeInAnimation>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 20px 32px', display: 'flex', flexDirection: 'column', gap: 20, marginTop: -16 }}>

          {/* About */}
          {therapist.description && (
            <FadeInAnimation delay={0.3}>
              <div className="card">
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: 'var(--color-primary)' }}>About</div>
                <p style={{ fontSize: 13, color: 'var(--color-on-surface-variant)', lineHeight: 1.6 }}>
                  {therapist.description}
                </p>
              </div>
            </FadeInAnimation>
          )}

          {/* Expertise */}
          {therapist.expertise && (
            <FadeInAnimation delay={0.35}>
              <div className="card">
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: 'var(--color-primary)' }}>Expertise</div>
                <p style={{ fontSize: 13, color: 'var(--color-on-surface-variant)' }}>{therapist.expertise}</p>
              </div>
            </FadeInAnimation>
          )}

          {/* Credits info */}
          <FadeInAnimation delay={0.4}>
            <div className="card" style={{ background: 'var(--color-surface-container)', border: 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-on-surface-variant)' }}>Your credits</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-primary)' }}>{credits}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--color-on-surface-variant)' }}>Session cost</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--color-on-surface)' }}>{therapist.rate}</div>
                </div>
              </div>
              {!hasCredits && (
                <div style={{
                  marginTop: 10, padding: '8px 12px', borderRadius: 8,
                  background: 'var(--color-error-container)',
                  fontSize: 12, color: 'var(--color-error)',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Insufficient credits.{' '}
                  <button onClick={() => navigate(ROUTES.SESSION)} style={{ background: 'none', color: 'var(--color-error)', fontWeight: 700, padding: 0, fontSize: 12 }}>
                    Buy more
                  </button>
                </div>
              )}
            </div>
          </FadeInAnimation>

          {/* Upcoming session */}
          {upcomingSession && (
            <FadeInAnimation delay={0.45}>
              <div className="card">
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: 'var(--color-primary)' }}>Upcoming Session</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{upcomingSession.sessionFrom ? formatDate(upcomingSession.sessionFrom) : ''}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-on-surface-variant)' }}>{upcomingSession.sessionFrom ? formatTime(upcomingSession.sessionFrom) : ''}</div>
                  </div>
                  {canJoin && upcomingSession.meetingLink && (
                    <button
                      onClick={() => navigate(ROUTES.CONFERENCE, { state: { session: upcomingSession, therapist } })}
                      style={{
                        padding: '8px 16px', borderRadius: 99,
                        background: 'var(--color-success)',
                        color: 'white', fontWeight: 700, fontSize: 13,
                      }}
                    >
                      Join Now
                    </button>
                  )}
                </div>
              </div>
            </FadeInAnimation>
          )}

          {/* Sessions history */}
          {sessions.length > 0 && (
            <FadeInAnimation delay={0.5}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: 'var(--color-primary)' }}>Session History</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sessions.slice(0, 3).map((s, i) => (
                    <div key={i} className="card" style={{ padding: '12px 16px', flexDirection: 'row', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{s.sessionFrom ? formatDate(s.sessionFrom) : 'Session'}</div>
                        <div style={{ fontSize: 12, color: 'var(--color-on-surface-variant)' }}>{s.sessionFrom ? formatTime(s.sessionFrom) : ''}</div>
                      </div>
                      <span style={{
                        padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                        background: s.status === 'COMPLETED' ? 'rgba(107,186,107,0.12)' : 'var(--color-surface-variant)',
                        color: s.status === 'COMPLETED' ? 'var(--color-success)' : 'var(--color-on-surface-variant)',
                      }}>
                        {s.status || 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeInAnimation>
          )}
        </div>
      </div>

      {/* Book Button */}
      <div style={{ padding: '12px 20px 28px', background: 'var(--color-surface)', borderTop: '1px solid var(--color-outline-variant)' }}>
        <button
          className="btn-gradient"
          onClick={() => {
            if (!hasCredits) {
              toast.error('Insufficient credits. Please purchase more.');
              navigate(ROUTES.SESSION);
              return;
            }
            // if (!isAvailable) {
            //   toast.error('This therapist is currently unavailable.');
            //   return;
            // }
            setBookingOpen(true);
          }}
          disabled={loading}
        >
          Book Session
        </button>
      </div>

      {/* Booking Sheet */}
      {bookingOpen && (
        <BookingSheet
          therapist={therapist}
          onClose={() => setBookingOpen(false)}
          onBooked={() => { setBookingOpen(false); loadData(); }}
        />
      )}
    </div>
  );
};

export default DocSummaryPage;
